// src/components/map/GoogleMapProvider.tsx
import React, { createContext, useContext, ReactNode } from 'react';
import { useJsApiLoader, Libraries } from '@react-google-maps/api';
import { Loader2 } from 'lucide-react';

// Libraries to load - defined outside component to prevent re-renders
const libraries: Libraries = ['places', 'drawing', 'geometry'];

interface GoogleMapContextValue {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapContext = createContext<GoogleMapContextValue>({
  isLoaded: false,
  loadError: undefined,
});

export const useGoogleMapContext = () => useContext(GoogleMapContext);

interface GoogleMapProviderProps {
  children: ReactNode;
}

const GoogleMapProvider: React.FC<GoogleMapProviderProps> = ({ children }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries,
  });

  if (!apiKey) {
    return (
      <div className="flex items-center justify-center p-8 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="text-center">
          <p className="text-amber-800 font-medium">Google Maps API Key Missing</p>
          <p className="text-amber-600 text-sm mt-1">
            Please add REACT_APP_GOOGLE_MAPS_API_KEY to your environment variables.
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center p-8 bg-red-50 border border-red-200 rounded-lg">
        <div className="text-center">
          <p className="text-red-800 font-medium">Failed to Load Google Maps</p>
          <p className="text-red-600 text-sm mt-1">{loadError.message}</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Loading Google Maps...</span>
      </div>
    );
  }

  return (
    <GoogleMapContext.Provider value={{ isLoaded, loadError }}>
      {children}
    </GoogleMapContext.Provider>
  );
};

export default GoogleMapProvider;
